import { Add } from '@mui/icons-material';
import { Button } from '@mui/material';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, CardBody, Col, Input, Row } from 'reactstrap';
import useAuth from '../../hooks/useAuth';
import RequireUserType from '../../routes/middleware/requireUserType';
import { getPurchaseOrderListReq } from '../../service/purchaseService';
import { setBreadcrumbItems } from '../../store/Breadcrumb/actions';
import { changePreloader } from '../../store/actions';
import {
  MODULES_ENUM,
  PERMISSIONS_ENUM,
  USER_TYPES_ENUM,
} from '../../utility/constants';
import { formatDate } from '../../utility/formatDate';
import { formatNumberWithCommasAndDecimal } from '../Invoices/invoiceUtil';
import DropdownMenuBtn from './DropdownMenuBtn';
import OrderStatusRenderer from './OrderStatusRenderer';

const ViewPurchaseOrder = (props) => {
  document.title = 'All Purchase Orders';
  let dispatch = useDispatch();
  let navigate = useNavigate();

  const autoSizeStrategy = {
    type: 'fitGridWidth',
  };
  const paginationPageSizeSelector = [25, 50, 100];
  const { auth } = useAuth();

  const [rowData, setRowData] = useState([]);
  const [paginationPageSize, setPaginationPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [sortData, setSortData] = useState({
    key: 'createdAt',
    order: 'desc',
  });

  const timerRef = useRef(null);
  const gridRef = useRef(null);

  const redirectToViewPage = (id) => {
    let path = `/purchase-orders/${id}`;
    setTimeout(() => {
      navigate(path, id);
    }, 300);
  };

  const handleViewClick = (id) => {
    redirectToViewPage(id.order_id);
  };

  useEffect(() => {
    props.setBreadcrumbItems('Purchase Orders', breadcrumbItems);
  }, []);

  useEffect(() => {
    let body = {
      page: page,
      limit: paginationPageSize,
    };
    if (searchValue) {
      body.search = searchValue;
    }
    if (sortData && sortData.key) {
      body.sort = sortData;
    }
    getListOfRowData(body);
  }, [page, paginationPageSize, searchValue, sortData]);

  const getListOfRowData = useCallback(
    async (body) => {
      dispatch(changePreloader(true));

      try {
        const response = await getPurchaseOrderListReq(body);

        if (!response?.data || !Array.isArray(response.data)) {
          setRowData([]);
          return;
        }

        const res = response.data.map((order) => ({
          order_id: order._id,
          purchaseOrderNumber: order.purchaseOrderNumber || '-',
          client_name: order.clientId?.name || '-',
          createdAt: order.createdAt,
          createdAtFormatted: formatDate(order.createdAt),
          total: order.grandTotal,
          status: order.POStatus || order.status,
          salesOrderNumber: order.salesOrderNumber || '-',
          purchaseRequisitionNumber: order.purchaseRequisitionNumber || '-',
          customerId: order?.zohoCustomerId,
          branch_name: order?.billing?.branchName || '-'
        }));

        setRowData(res);
      } catch (error) {
        console.error('Error fetching purchase orders:', error);
        setRowData([]);
      } finally {
        dispatch(changePreloader(false));
      }
    },
    [dispatch],
  );

  const redirectToEditPage = (id) => {
    let path = `/purchase-orders/${id.order_id}/edit`;
    setTimeout(() => {
      navigate(path);
    }, 300);
  };
  
  const handleDeleteResponse = (response) => {
    if (response.success === true) {
      notify('Success', response.message);
    } else {
      notify('Error', response.message);
    }
    getListOfRowData({
      page: page,
      limit: paginationPageSize,
      search: searchValue,
      sort: sortData
    });
  };

  const handleEditClick = (id) => {
    redirectToEditPage(id);
  };

  const onPaginationChanged = useCallback(() => {
    if (!gridRef.current) return;
    
    const pageSize = gridRef.current.api.paginationGetPageSize();
    if (pageSize !== paginationPageSize) {
      setPaginationPageSize(pageSize);
    }
    
    const newPage = gridRef.current.api.paginationGetCurrentPage() + 1;
    if (page !== newPage) {
      setPage(newPage);
    }
  }, [page, paginationPageSize]);

  const handleInputChange = (e) => {
    const value = e.target.value?.trim() || '';
    setInputValue(value);
    
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setRowData([]);
      setPage(1);
      setSearchValue(value);
    }, 2000);
  };

  const breadcrumbItems = [
    { title: 'Dashboard', link: '/dashboard' },
    { title: 'Purchase Orders', link: '#' },
  ];

  const columnDefs = [
    {
      headerName: 'PO No.',
      field: 'purchaseOrderNumber',
      sortable: true,
      minWidth: 150,
      cellRenderer: (params) => {
        const id = params.data?.order_id;
        const number = params.value;

        const isClickable = number && number.trim() !== '' && number.trim() !== '-';

        return (
          <span
            style={{
              cursor: id ? 'pointer' : 'default',
              color: id ? '#007bff' : 'inherit',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (id) navigate(`/purchase-orders/${id}`);
            }}
          >
            {number || '-'}
          </span>
        );
      },
    },
    {
      headerName: 'PR No.',
      field: 'purchaseRequisitionNumber',
      sortable: true,
      minWidth: 150,
      cellRenderer: (params) => {
        const id = params.data?.order_id;
        const number = params.data?.purchaseRequisitionNumber;

        const isClickable = number && number.trim() !== '' && number.trim() !== '-';

        return (
          <span
            style={{
              cursor: isClickable ? 'pointer' : 'default',
              color: isClickable ? '#007bff' : 'inherit',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (isClickable && id) {
                navigate(`/purchase-requisitions/${id}`);
              }
            }}
          >
            {isClickable ? number : '-'}
          </span>
        );
      },
    },
    {
      headerName: 'SO No.',
      field: 'salesOrderNumber',
      sortable: true,
      minWidth: 140,
      cellRenderer: (params) => {
        const salesOrderId = params.data?.salesOrderNumber;
        const number = params.data?.salesOrderNumber;

        const isClickable = number && number.trim() !== '' && number.trim() !== '-';

        return (
          <span
            style={{
              cursor: number ? 'pointer' : 'default',
              color: number ? '#007bff' : 'inherit',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (salesOrderId) {
                navigate(`/order/${params.data?.customerId}`);
              }
            }}
          >
            {number || '-'}
          </span>
        );
      },
    },
    {
      headerName: 'Branch',
      field: 'branch_name',
      sortable: true,
      minWidth: 250,
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value,
      headerTooltip: "Branch",
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        textAlign: 'left',
      },
    },
    {
      headerName: 'Order Date',
      field: 'createdAt',
      sortable: true,
      minWidth: 150,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Order Status", 
      field: "status", 
      sortable: true,
      minWidth: 150,
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value, 
      headerTooltip: "Order Status",
      cellStyle: {
        textAlign: "left",
      },
      cellRenderer: (params) => (
        <OrderStatusRenderer value={params.value} type="po" />
      ),
    },
    {
      headerName: 'Client',
      field: 'client_name',
      sortable: true,
      minWidth: 150,
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value,
      headerTooltip: 'Client',
    },
    {
      headerName: 'Total Amount',
      field: 'total',
      sortable: true,
      minWidth: 150,
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value,
      headerTooltip: 'Total Amount',
      valueFormatter: (params) =>
        formatNumberWithCommasAndDecimal(params.value) + ' /-',
    },
    {
      headerName: 'Action',
      field: 'action',
      sortable: false,
      width: 100,
      cellClass: 'actions-button-cell',
      cellRenderer: DropdownMenuBtn,
      cellRendererParams: {
        handleResponse: handleDeleteResponse,
        handleEditClick: handleEditClick,
        handleViewClick: handleViewClick,
      },
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value,
      headerTooltip: 'Actions',
    },
  ];

  const clientColumnDefs = columnDefs.filter(
    (colDef) => colDef.headerName !== 'Client',
  );
  
  const clientUserColumnDefs = columnDefs.filter(
    (colDef) => colDef.headerName !== 'Total Amount',
  );

  const notify = (type, message) => {
    if (type === 'Error') {
      toast.error(message, {
        position: 'top-center',
        theme: 'colored',
      });
    } else {
      toast.success(message, {
        position: 'top-center',
        theme: 'colored',
      });
    }
  };

  const handleSortChanged = useCallback((event) => {
    if (!gridRef.current) return;
    
    const columnState = gridRef.current.api.getColumnState();
    const sortedColumn = columnState.find(col => col.sort);
    
    if (sortedColumn) {
      const newSortData = {
        key: sortedColumn.colId,
        order: sortedColumn.sort
      };
      
      if (sortData?.key !== newSortData.key || sortData?.order !== newSortData.order) {
        setSortData(newSortData);
        setPage(1);
        setRowData([]); 
      }
    } else {
      if (sortData !== null) {
        setSortData(null);
        setPage(1);
        setRowData([]);
      }
    }
  }, [sortData]);

  const onGridReady = useCallback((params) => {
    if (sortData && sortData.key && sortData.order) {
      params.api.applyColumnState({
        state: [{ colId: sortData.key, sort: sortData.order }],
        defaultState: { sort: null },
      });
    }
  }, [sortData]);

  return (
    <>
      <div className="all-items">
        <Row>
          <Col className="col-12">
            <Card>
              <CardBody>
                <div className="button-section">
                  <div className="button-right-section">
                    <div className="invoice-search-box">
                      <div
                        className="search-box position-relative"
                        style={{
                          width:
                            auth.userType === USER_TYPES_ENUM.ADMIN
                              ? '20rem'
                              : '14rem',
                        }}
                      >
                        <Input
                          type="text"
                          value={inputValue}
                          onChange={handleInputChange}
                          className="form-control rounded border"
                          placeholder={
                            auth.userType === USER_TYPES_ENUM.ADMIN
                              ? 'Search by Client or Order Number'
                              : 'Search by Order Number'
                          }
                        />
                        <i className="mdi mdi-magnify search-icon"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className="ag-theme-quartz"
                  style={{
                    height: '550px',
                    width: '100%',
                  }}
                >
                  <RequireUserType userType={USER_TYPES_ENUM.ADMIN}>
                    <AgGridReact
                      ref={gridRef}
                      rowData={rowData}
                      columnDefs={columnDefs}
                      pagination={true}
                      paginationPageSize={paginationPageSize}
                      paginationPageSizeSelector={paginationPageSizeSelector}
                      autoSizeStrategy={autoSizeStrategy}
                      onPaginationChanged={onPaginationChanged}
                      onSortChanged={handleSortChanged}
                      onGridReady={onGridReady}
                      sortingOrder={['asc', 'desc']}
                      animateRows={true}
                      reactiveCustomComponents
                      suppressRowClickSelection={true}
                    />
                  </RequireUserType>
                  <RequireUserType userType={USER_TYPES_ENUM.CLIENT}>
                    <AgGridReact
                      ref={gridRef}
                      rowData={rowData}
                      columnDefs={
                        auth?.permissions?.some(
                          (p) =>
                            p.module === MODULES_ENUM.ORDERS &&
                            [
                              PERMISSIONS_ENUM.READ,
                              PERMISSIONS_ENUM.CREATE,
                            ].every((perm) => p.operations.includes(perm)),
                        )
                          ? clientColumnDefs
                          : auth?.permissions?.some(
                            (p) =>
                              p.module === MODULES_ENUM.ORDERS &&
                              p.operations.includes(PERMISSIONS_ENUM.READ),
                          )
                            ? clientUserColumnDefs
                            : []
                      }
                      pagination={true}
                      paginationPageSize={paginationPageSize}
                      paginationPageSizeSelector={paginationPageSizeSelector}
                      autoSizeStrategy={autoSizeStrategy}
                      onPaginationChanged={onPaginationChanged}
                      onSortChanged={handleSortChanged}
                      onGridReady={onGridReady}
                      sortingOrder={['asc', 'desc']}
                      animateRows={true}
                      reactiveCustomComponents
                      suppressRowClickSelection={true}
                    />
                  </RequireUserType>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default connect(null, { setBreadcrumbItems })(ViewPurchaseOrder);
